/**
 * Les pièces obligatoires d'un sous-traitant, et leurs échéances.
 *
 * Décidé avec le patron le 30 octobre 2026, troisième volet du phasage.
 * Celui qui confie un marché doit se faire remettre les pièces du
 * sous-traitant et les **redemander à temps** — c'est l'obligation de
 * vigilance (Code du travail, L.8222-1 et D.8222-5) : à défaut, il répond
 * solidairement de ce que le sous-traitant n'a pas déclaré. Les assurances
 * s'y ajoutent : sans décennale, un sinistre sur le lot sous-traité retombe
 * sur l'entreprise principale.
 *
 * Même logique que les papiers des équipes (`certifications`) : une pièce
 * n'est pas urgente le jour où elle expire, mais le jour où il faut la
 * redemander. Tout ici est pur.
 */
import { daysUntil } from "../deadlines/types.js";
import type { VigilanceBasis } from "../config/types.js";

export type SubcontractorDocumentKind =
  | "urssaf"
  | "kbis"
  | "decennale"
  | "rc_pro"
  | "salaries_etrangers"
  | "autre";

export interface SubcontractorDocument {
  id: string;
  subcontractorId: string;
  kind: SubcontractorDocumentKind;
  /** L'assureur, le numéro de police, l'intitulé d'une pièce « autre ». */
  label: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  /** Dans le bucket `documents`. Nul pour une pièce notée sans son fichier. */
  storagePath: string | null;
  fileName: string | null;
  notes: string | null;
  createdAt: string;
}

interface DocumentRule {
  label: string;
  /** Exigée de tout sous-traitant à qui un marché est confié. */
  required: boolean;
  /**
   * Une pièce de l'obligation de vigilance : celle que le Code du travail
   * n'impose qu'au-delà du seuil, quand le patron choisit de l'appliquer.
   */
  vigilance: boolean;
  /**
   * La durée de validité, en mois, pour **proposer** une fin depuis la date
   * d'émission. `null` quand c'est la pièce qui la dit — une attestation
   * d'assurance porte sa période.
   */
  validityMonths: number | null;
  /** Combien de jours avant la fin il faut la redemander. */
  leadDays: number;
  hint: string;
}

export const SUBCONTRACTOR_DOCUMENT_RULES: Record<SubcontractorDocumentKind, DocumentRule> = {
  urssaf: {
    vigilance: true,
    label: "Attestation URSSAF",
    required: true,
    validityMonths: 6,
    leadDays: 30,
    hint: "L'attestation de vigilance, à redemander tous les six mois jusqu'à la fin du marché.",
  },
  kbis: {
    vigilance: true,
    label: "Extrait Kbis",
    required: true,
    validityMonths: 3,
    leadDays: 15,
    hint: "Un extrait de moins de trois mois (ou la carte d'artisan pour une entreprise au répertoire des métiers).",
  },
  decennale: {
    vigilance: false,
    label: "Assurance décennale",
    required: true,
    validityMonths: null,
    leadDays: 30,
    hint: "L'attestation couvre une période et des activités : vérifiez que le lot confié y figure.",
  },
  rc_pro: {
    vigilance: false,
    label: "RC professionnelle",
    required: true,
    validityMonths: null,
    leadDays: 30,
    hint: "La fin de la période d'assurance figure sur l'attestation.",
  },
  salaries_etrangers: {
    vigilance: true,
    label: "Liste des salariés étrangers",
    required: false,
    validityMonths: 6,
    leadDays: 30,
    hint: "Exigée si le sous-traitant emploie des salariés étrangers soumis à autorisation de travail.",
  },
  autre: {
    vigilance: false,
    label: "Autre pièce",
    required: false,
    validityMonths: null,
    leadDays: 30,
    hint: "",
  },
};

export const SUBCONTRACTOR_DOCUMENT_KINDS = Object.keys(
  SUBCONTRACTOR_DOCUMENT_RULES,
) as SubcontractorDocumentKind[];

export const REQUIRED_SUBCONTRACTOR_DOCUMENTS = SUBCONTRACTOR_DOCUMENT_KINDS.filter(
  (k) => SUBCONTRACTOR_DOCUMENT_RULES[k].required,
);

/**
 * Le seuil de l'obligation de vigilance (L.8222-1) : en dessous, l'attestation
 * URSSAF et le Kbis ne s'imposent pas. Il ne compte que si le patron l'a
 * activé dans les paramètres ; par défaut, tout marché les réclame.
 */
export const VIGILANCE_THRESHOLD_HT = 5000;

/**
 * Ce qui se compare au seuil, pour chaque sous-traitant, sur ses marchés à
 * signer ou signés, tous chantiers confondus — au choix du patron :
 *
 *   · `cumulative` — leur somme HT : trois marchés de 2 000 € en font un de
 *     6 000 €, et le découpage ne fait pas taire la vigilance ;
 *   · `per_contract` — le plus gros d'entre eux, chaque marché pris seul.
 */
export function engagedHtBySubcontractor(
  contracts: readonly { subcontractorId: string; status: string; amountHt: number }[],
  basis: VigilanceBasis = "cumulative",
): Map<string, number> {
  const total = new Map<string, number>();
  for (const c of contracts) {
    if (c.status !== "draft" && c.status !== "signed") continue;
    const avant = total.get(c.subcontractorId) ?? 0;
    total.set(c.subcontractorId, basis === "cumulative" ? avant + c.amountHt : Math.max(avant, c.amountHt));
  }
  return total;
}

/** L'attestation URSSAF et le Kbis sont-ils exigés de ce sous-traitant ? */
export function vigilanceRequired(engagedHt: number, applyThreshold: boolean): boolean {
  return !applyThreshold || engagedHt >= VIGILANCE_THRESHOLD_HT;
}

/** Les natures exigées, selon que la vigilance s'impose ou non. */
export function requiredDocumentKinds(vigilance = true): SubcontractorDocumentKind[] {
  return REQUIRED_SUBCONTRACTOR_DOCUMENTS.filter((k) => vigilance || !SUBCONTRACTOR_DOCUMENT_RULES[k].vigilance);
}

/** « Assurance décennale — SMABTP », ou le libellé seul. */
export function subcontractorDocumentName(d: Pick<SubcontractorDocument, "kind" | "label">): string {
  const base = SUBCONTRACTOR_DOCUMENT_RULES[d.kind].label;
  const precision = d.label?.trim();
  return precision ? `${base} — ${precision}` : base;
}

/**
 * La fin de validité à proposer, depuis la date d'émission. Au mois
 * calendaire : une attestation du 31 août vaut jusqu'au 28 février.
 * Proposée, jamais imposée.
 */
export function suggestedDocumentExpiry(kind: SubcontractorDocumentKind, issuedOn: string | null): string | null {
  const mois = SUBCONTRACTOR_DOCUMENT_RULES[kind].validityMonths;
  if (mois == null || !issuedOn) return null;
  const [a, m, j] = issuedOn.slice(0, 10).split("-").map(Number) as [number, number, number];
  const total = m - 1 + mois;
  const annee = a + Math.floor(total / 12);
  const moisCible = (total % 12) + 1;
  const dernierJour = new Date(Date.UTC(annee, moisCible, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${annee}-${pad(moisCible)}-${pad(Math.min(j, dernierJour))}`;
}

/**
 * L'état d'une pièce exigée, du plus pressant au plus tranquille :
 *
 *   · `missing`  — jamais reçue ;
 *   · `expired`  — la plus récente ne vaut plus ;
 *   · `expiring` — elle vaut encore, mais il est temps de la redemander ;
 *   · `valid`    — rien à faire ;
 *   · `undated`  — reçue sans date de fin : on ne sait pas, et on le dit.
 */
export type DocumentStatus = "missing" | "expired" | "expiring" | "valid" | "undated";

const RANG: Record<DocumentStatus, number> = { missing: 0, expired: 1, expiring: 2, undated: 3, valid: 4 };

export function documentStatus(
  doc: Pick<SubcontractorDocument, "kind" | "expiresOn"> | null,
  today: string,
): DocumentStatus {
  if (!doc) return "missing";
  if (!doc.expiresOn) return "undated";
  const jours = daysUntil(doc.expiresOn, today);
  if (jours < 0) return "expired";
  return jours <= SUBCONTRACTOR_DOCUMENT_RULES[doc.kind].leadDays ? "expiring" : "valid";
}

/** Ce qui demande un geste : manquante, expirée, ou bientôt. */
export function documentNeedsAttention(status: DocumentStatus): boolean {
  return status === "missing" || status === "expired" || status === "expiring";
}

/**
 * La pièce qui fait foi pour une nature : celle qui vaut le plus longtemps.
 * Une attestation déposée en avance ne doit pas être masquée par l'ancienne,
 * ni l'inverse. Sans date de fin, la plus récemment déposée.
 */
export function currentDocument(
  docs: readonly SubcontractorDocument[],
  kind: SubcontractorDocumentKind,
): SubcontractorDocument | null {
  const duType = docs.filter((d) => d.kind === kind);
  if (duType.length === 0) return null;
  return [...duType].sort(
    (a, b) =>
      (b.expiresOn ?? "").localeCompare(a.expiresOn ?? "") || b.createdAt.localeCompare(a.createdAt),
  )[0]!;
}

export interface RequiredDocumentState {
  kind: SubcontractorDocumentKind;
  document: SubcontractorDocument | null;
  status: DocumentStatus;
  /** Jours avant la fin ; négatif une fois passée ; nul sans date. */
  daysLeft: number | null;
}

/**
 * Les pièces exigées d'un sous-traitant, chacune avec son état. Sans
 * vigilance (seuil appliqué et non atteint), les assurances seules.
 */
export function requiredDocumentsState(
  docs: readonly SubcontractorDocument[],
  today: string,
  vigilance = true,
): RequiredDocumentState[] {
  return requiredDocumentKinds(vigilance).map((kind) => {
    const document = currentDocument(docs, kind);
    return {
      kind,
      document,
      status: documentStatus(document, today),
      daysLeft: document?.expiresOn ? daysUntil(document.expiresOn, today) : null,
    };
  });
}

/** Le pire état des pièces exigées : ce que la liste affiche d'un mot. */
export function complianceStatus(
  docs: readonly SubcontractorDocument[],
  today: string,
  vigilance = true,
): DocumentStatus {
  return requiredDocumentsState(docs, today, vigilance).reduce<DocumentStatus>(
    (pire, e) => (RANG[e.status] < RANG[pire] ? e.status : pire),
    "valid",
  );
}

export interface DocumentDue {
  subcontractorId: string;
  subcontractorName: string;
  kind: SubcontractorDocumentKind;
  status: DocumentStatus;
  daysLeft: number | null;
}

/**
 * Les pièces à réclamer, pour les sous-traitants **qui ont un marché en
 * cours** — à signer ou signé. Un sous-traitant du carnet sans marché, ou
 * dont le marché est terminé, ne demande aucun geste : le relancer pour rien
 * ferait douter du reste de la liste. Le plus pressant d'abord.
 *
 * Seuil appliqué, un sous-traitant sous les 5 000 € HT — en cumul ou marché
 * par marché, selon le réglage — ne se voit réclamer que ses assurances.
 */
export function subcontractorDocumentsDue(
  subcontractors: readonly { id: string; name: string; archived: boolean }[],
  contracts: readonly { subcontractorId: string; status: string; amountHt: number }[],
  docs: readonly SubcontractorDocument[],
  today: string,
  applyVigilanceThreshold = false,
  basis: VigilanceBasis = "cumulative",
): DocumentDue[] {
  const engages = engagedHtBySubcontractor(contracts, basis);
  return subcontractors
    .filter((s) => !s.archived && engages.has(s.id))
    .flatMap((s) =>
      requiredDocumentsState(
        docs.filter((d) => d.subcontractorId === s.id),
        today,
        vigilanceRequired(engages.get(s.id) ?? 0, applyVigilanceThreshold),
      )
        .filter((e) => documentNeedsAttention(e.status))
        .map((e) => ({
          subcontractorId: s.id,
          subcontractorName: s.name,
          kind: e.kind,
          status: e.status,
          daysLeft: e.daysLeft,
        })),
    )
    .sort(
      (a, b) =>
        RANG[a.status] - RANG[b.status] ||
        (a.daysLeft ?? -Infinity) - (b.daysLeft ?? -Infinity) ||
        a.subcontractorName.localeCompare(b.subcontractorName, "fr"),
    );
}

/** Taille et types de fichier acceptés : ceux des documents de chantier. */
export const SUBCONTRACTOR_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;
const TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic"] as const;

export function subcontractorDocumentProblem(file: { name: string; type: string; size: number }): string | null {
  if (file.size <= 0) return "Ce fichier est vide.";
  if (file.size > SUBCONTRACTOR_DOCUMENT_MAX_BYTES) {
    return `Ce fichier dépasse 25 Mo (${(file.size / 1024 / 1024).toFixed(1).replace(".", ",")} Mo).`;
  }
  if (!(TYPES as readonly string[]).includes(file.type)) {
    return "Ce type de fichier n'est pas accepté : un PDF ou une photo.";
  }
  if (file.name.trim().length === 0 || file.name.length > 200) {
    return "Le nom du fichier doit faire entre 1 et 200 caractères.";
  }
  return null;
}

/** `<entreprise>/sous-traitants/<sous-traitant>/<id>-<nom>` dans le bucket `documents`. */
export function subcontractorDocumentPath(
  tenantId: string,
  subcontractorId: string,
  id: string,
  fileName: string,
): string {
  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "") : "";
  const safe =
    stem
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^A-Za-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "piece";
  return `${tenantId}/sous-traitants/${subcontractorId}/${id}-${safe}${ext ? `.${ext}` : ""}`;
}
